import { memo, useEffect, useRef, useState } from 'react';
import clsx from 'clsx';
import { Check, Pencil, Trash2, X } from 'lucide-react';

import { ConversationSummary } from '../types/chat.types';

interface ConversationListItemProps {
  conversation: ConversationSummary;
  isActive: boolean;
  formattedDate: string;
  onSelect: (conversationId: string) => void;
  onRename?: (conversationId: string, name: string) => Promise<void> | void;
  onRequestDelete: (conversation: ConversationSummary) => void;
}

// Actions are plain buttons on purpose. They used to be a Radix DropdownMenu
// plus a Radix AlertDialog *per row* — two full component trees, with their own
// effects, refs and ids, mounted once per conversation for a menu that is
// almost never opened. With 60 conversations that was 120 Radix trees built
// every time the panel opened. Two icon buttons cost nothing, and the single
// delete confirmation now lives once, in the parent list.
const ACTION_BUTTON_CLASS =
  'flex h-7 w-7 shrink-0 items-center justify-center rounded-md text-slate-400 ' +
  'transition-colors hover:bg-slate-200 hover:text-slate-700 ' +
  'focus-visible:opacity-100 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary ' +
  // Always reachable on touch; on desktop they appear with the row's hover.
  'opacity-100 md:opacity-0 md:group-hover:opacity-100 md:group-focus-within:opacity-100';

export const ConversationListItem = memo(
  ({
    conversation,
    isActive,
    formattedDate,
    onSelect,
    onRename,
    onRequestDelete,
  }: ConversationListItemProps) => {
    const [isEditing, setIsEditing] = useState(false);
    const [draftName, setDraftName] = useState(conversation.name);
    const [isSaving, setIsSaving] = useState(false);
    const inputRef = useRef<HTMLInputElement>(null);

    useEffect(() => {
      if (!isEditing) {
        setDraftName(conversation.name);
      }
    }, [conversation.name, isEditing]);

    useEffect(() => {
      if (isEditing) {
        inputRef.current?.focus();
        inputRef.current?.select();
      }
    }, [isEditing]);

    const handleCancel = () => {
      setDraftName(conversation.name);
      setIsEditing(false);
    };

    const handleSubmit = async () => {
      const trimmed = draftName.trim();

      if (!onRename || !trimmed || trimmed === conversation.name) {
        handleCancel();
        return;
      }

      setIsSaving(true);
      try {
        await onRename(conversation.conversationId, trimmed);
        setIsEditing(false);
      } catch (error) {
        console.error('[ConversationListItem] Error renaming conversation', error);
      } finally {
        setIsSaving(false);
      }
    };

    if (isEditing) {
      return (
        <div className="flex items-center gap-1 rounded-lg px-3 py-2">
          <input
            ref={inputRef}
            type="text"
            value={draftName}
            onChange={(event) => setDraftName(event.target.value)}
            onKeyDown={(event) => {
              if (event.key === 'Enter') {
                event.preventDefault();
                void handleSubmit();
              } else if (event.key === 'Escape') {
                event.preventDefault();
                handleCancel();
              }
            }}
            onBlur={(event) => {
              const action = (event.relatedTarget as HTMLElement | null)?.dataset?.action;
              if (action === 'rename-save' || action === 'rename-cancel') return;
              handleCancel();
            }}
            disabled={isSaving}
            className="w-full min-w-0 rounded-md border border-slate-300 px-2 py-1 text-base text-slate-900 focus:border-primary focus:outline-none focus:ring-2 focus:ring-primary/20"
            aria-label="Rename conversation"
          />
          <button
            type="button"
            data-action="rename-save"
            onClick={() => void handleSubmit()}
            disabled={isSaving}
            aria-label="Save new name"
            className={ACTION_BUTTON_CLASS}
          >
            <Check className="h-4 w-4" />
          </button>
          <button
            type="button"
            data-action="rename-cancel"
            onClick={handleCancel}
            disabled={isSaving}
            aria-label="Cancel renaming"
            className={ACTION_BUTTON_CLASS}
          >
            <X className="h-4 w-4" />
          </button>
        </div>
      );
    }

    return (
      <div
        role="button"
        tabIndex={0}
        className={clsx(
          'group flex w-full items-start gap-2 rounded-lg px-3 py-2 text-left transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary',
          isActive
            ? 'bg-slate-200 text-slate-900'
            : 'text-slate-600 hover:bg-slate-100 hover:text-slate-900'
        )}
        onClick={() => onSelect(conversation.conversationId)}
        onKeyDown={(event) => {
          if (event.key === 'Enter' || event.key === ' ') {
            event.preventDefault();
            onSelect(conversation.conversationId);
          }
        }}
      >
        <div className="min-w-0 flex-1 space-y-1">
          <p className="line-clamp-2 text-sm font-medium text-slate-900">{conversation.name}</p>
          <span className="block text-xs text-slate-400">{formattedDate}</span>
        </div>

        {onRename && (
          <button
            type="button"
            aria-label={`Rename ${conversation.name}`}
            className={ACTION_BUTTON_CLASS}
            onClick={(event) => {
              event.stopPropagation();
              setDraftName(conversation.name);
              setIsEditing(true);
            }}
          >
            <Pencil className="h-3.5 w-3.5" />
          </button>
        )}

        <button
          type="button"
          aria-label={`Delete ${conversation.name}`}
          className={ACTION_BUTTON_CLASS}
          onClick={(event) => {
            event.stopPropagation();
            onRequestDelete(conversation);
          }}
        >
          <Trash2 className="h-3.5 w-3.5" />
        </button>
      </div>
    );
  },
);

ConversationListItem.displayName = 'ConversationListItem';
